import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import ChipFeedInDfv from '../../ChipFeed/ChipFeedInDfv';
import DynamicFieldViewLiveDataContainer from '../../DynamicFieldView/containers/DynamicFieldViewLiveDataContainer';
import { MotionDfvChipFeedWrapper } from '../DfvChipFeed.style';
import { ErrorBoundary } from 'react-error-boundary';
import { AnimatePresence } from 'framer-motion';
import { getDynamicFieldViewLiveState } from '../../DynamicFieldView/store/selectors/getDynamicFieldViewLive';
import usePlayTimer from '@gameday/utils/hooks/usePlayTimer';
import playTypeMap from '../../DynamicFieldView/utils/playTypeMap';
import { getCommercialBreakState } from '../../Ads/store/selectors/commercialBreak';
import logArgs from '@gameday/utils/logArgs';
let enableDFVLog = false;

if (typeof URLSearchParams !== 'undefined' && typeof location !== 'undefined') {
    const params = new URLSearchParams(location.search);
    const dfvLogParam = params.get('enableDFVLog');
    if (dfvLogParam === 'true') {
        enableDFVLog = dfvLogParam;
    }
}

const { PITCH_WITH_RESULT, RESULT, RESULT_NO_IN_PLAY, PITCH } = playTypeMap;
const EASE_OUT = 'easeOut';
const initial = { opacity: 0, zIndex: -1 };
const easeInSeconds = 1.5;
const easeOutSeconds = 0.375;
const animate = {
    [PITCH]: { opacity: [0, 1, 1, 1], zIndex: [-1, 1, 1, 1] },
    [RESULT]: { opacity: [1, 1, 1, 0], zIndex: [1, 1, 1, -1] },
    [RESULT_NO_IN_PLAY]: { opacity: [0, 1, 1, 0], zIndex: [-1, 1, 1, -1] },
    [PITCH_WITH_RESULT]: { opacity: [0, 1, 1, 0], zIndex: [-1, 1, 1, -1] }
};
const exit = {
    opacity: 0,
    zIndex: 0,
    transition: { duration: easeOutSeconds, ease: EASE_OUT }
};

const EMPTY_OBJECT = Object.freeze({});
const FallbackComponent = () => {
    return null;
};

const DFVChipFeedDataContainer = () => {
    const dfvState = useSelector(getDynamicFieldViewLiveState);
    const { enableDFVMobile, disable, currentPlay, playType, playStartTime, playEndTime, allPlays } = dfvState;
    const isResult = playType === PITCH_WITH_RESULT || playType === PITCH || playType === RESULT || playType === RESULT_NO_IN_PLAY;
    const { timer, timerNum, delay } = usePlayTimer(isResult) || EMPTY_OBJECT;
    const { adStarted, showMidInnigAd } = useSelector(getCommercialBreakState);
    const wrapperRef = useRef(null);

    const transition = useMemo(() => {
        const easeInPercentage = `${easeInSeconds / timerNum}`;
        const easeOutPercentage = `${(timerNum - easeOutSeconds) / timerNum}`;
        return {
            duration: timerNum,
            ease: EASE_OUT,
            times: [0, easeInPercentage, easeOutPercentage, 1],
            delay
        };
    }, [timerNum, delay]);

    logArgs(enableDFVLog, 'animation:: DFV', {
        playType,
        timer,
        currentPlay,
        playStart: playStartTime,
        playEnd: playEndTime,
        dfvState,
        isResult,
        dataCallTime: new Date(),
        allPlays
    });

    const DFVCom = useMemo(() => {
        return isResult && (
            <MotionDfvChipFeedWrapper
                key="dfv-chip-feed"
                initial={initial}
                animate={animate[playType]}
                exit={exit}
                transition={transition}
                ref={wrapperRef}
                onUpdate={(latest) => {
                    if (wrapperRef.current) {
                        wrapperRef.current.style.zIndex = latest.opacity === 0 ? '-1' : '0';
                    }
                }}
            >
                <DynamicFieldViewLiveDataContainer />
                <ChipFeedInDfv />
            </MotionDfvChipFeedWrapper>
        );
    }, [isResult, playStartTime, transition, playType, wrapperRef]);

    if (!enableDFVMobile || disable || (showMidInnigAd && adStarted)) {
        return null;
    }

    return (
        <ErrorBoundary FallbackComponent={FallbackComponent}>
            <AnimatePresence mode="wait">
                {DFVCom}
            </AnimatePresence>
        </ErrorBoundary>
    );
};

export default DFVChipFeedDataContainer;
